import React from 'react';
import type { ItemData } from '../../components/ItemKit';
import { ItemIcon } from '../../components/ItemIcon';
import { humanize, rarityTone, type EquipmentSlotDefinition, type EquipmentDropState } from './characterPresentation';
import { TRAIL_ITEM_SLOT } from '../../../../shared/trailStyle';
import { FLAME_ITEM_SLOT, LANTERN_ITEM_SLOT } from '../../../../shared/lanternStyle';

/** Empty Trail slot: three fading motes, the shape the slot's item gives the ghost. */
const TrailSlotGlyph: React.FC = () => (
  <svg width="40" height="24" viewBox="0 0 40 24" data-slot-glyph="trail">
    <circle cx="31" cy="10" r="5" fill="currentColor" opacity="0.7" />
    <circle cx="19" cy="12" r="3.8" fill="currentColor" opacity="0.45" />
    <circle cx="9" cy="14" r="2.6" fill="currentColor" opacity="0.25" />
  </svg>
);

/** Empty Lantern slot: an unlit lantern outline with a faint halo (lane BK). */
const LanternSlotGlyph: React.FC = () => (
  <svg width="28" height="32" viewBox="0 0 28 32" data-slot-glyph="lantern">
    <circle cx="14" cy="18" r="11" fill="currentColor" opacity="0.12" />
    <path d="M10 7 Q14 1 18 7" fill="none" stroke="currentColor" strokeWidth="1.6" opacity="0.6" />
    <rect x="8.5" y="8" width="11" height="17" rx="3" fill="none" stroke="currentColor" strokeWidth="1.6" opacity="0.6" />
    <ellipse cx="14" cy="16.5" rx="2.2" ry="3.2" fill="currentColor" opacity="0.35" />
  </svg>
);

/** Empty Flame slot: an unlit flame outline floating over a faint head line. */
const FlameSlotGlyph: React.FC = () => (
  <svg width="28" height="32" viewBox="0 0 28 32" data-slot-glyph="flame">
    <circle cx="14" cy="13" r="10" fill="currentColor" opacity="0.12" />
    <path d="M13.4 3 C14.6 6.6 19 9 19 13.6 C19 17.2 16.8 19.4 14 19.4 C11.2 19.4 9 17.2 9 13.6 C9 10.2 12.2 8 13.4 3 Z" fill="none" stroke="currentColor" strokeWidth="1.6" opacity="0.6" />
    <path d="M7 28 Q14 24.5 21 28" fill="none" stroke="currentColor" strokeWidth="1.4" opacity="0.35" />
  </svg>
);

const emptyGlyph = (slotId: string): React.ReactNode =>
  slotId === TRAIL_ITEM_SLOT ? <TrailSlotGlyph />
    : slotId === LANTERN_ITEM_SLOT ? <LanternSlotGlyph />
      : slotId === FLAME_ITEM_SLOT ? <FlameSlotGlyph /> : '—';

interface CharacterEquipmentSlotProps {
  definition: EquipmentSlotDefinition;
  item: ItemData | null;
  selected: boolean;
  dropState: EquipmentDropState;
  onSelect: () => void;
  onDragEnter: React.DragEventHandler<HTMLButtonElement>;
  onDragOver: React.DragEventHandler<HTMLButtonElement>;
  onDragLeave: React.DragEventHandler<HTMLButtonElement>;
  onDrop: React.DragEventHandler<HTMLButtonElement>;
}

export const CharacterEquipmentSlot: React.FC<CharacterEquipmentSlotProps> = ({
  definition,
  item,
  selected,
  dropState,
  onSelect,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
}) => {
  const state = item ? `occupied, ${item.name}` : 'empty';
  const toneStyle = {
    '--character-slot-tone': rarityTone(item),
  } as React.CSSProperties;

  return (
    <button
      type="button"
      className="character-equipment-slot"
      style={toneStyle}
      aria-label={`${definition.label} slot, ${state}`}
      aria-pressed={selected}
      data-slot-id={definition.id}
      data-slot-state={item ? 'occupied' : 'empty'}
      data-drop-state={dropState}
      onClick={onSelect}
      onContextMenu={(event) => {
        event.preventDefault();
        onSelect();
      }}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <span className="character-equipment-slot__icon" aria-hidden="true">
        {item ? <ItemIcon itemId={item.id} size={48} /> : emptyGlyph(definition.id)}
      </span>
      <span className="character-equipment-slot__copy">
        <span className="character-equipment-slot__label">{definition.label}</span>
        <span className="character-equipment-slot__item">{item?.name ?? 'Empty'}</span>
      </span>
      <span className="character-equipment-slot__status" aria-hidden="true">
        {dropState === 'valid'
          ? 'Release'
          : dropState === 'available'
            ? 'Ready'
            : dropState === 'invalid'
              ? 'Wrong slot'
              : selected
                ? 'Selected'
                : item
                  ? humanize(item.rarity ?? 'equipped')
                  : 'Open'}
      </span>
    </button>
  );
};
